<template>
	<AppAutoSuggest
		:options="options"
		:active-item="activeItem"
		:auto-navigation="autoNavigation"
		:is-loading="isLoading"
		:placeholder="$t('autosuggests.projects.placeholder')"
		@input="$emit('input', $event)"
		@load="loadOptions($event)"
	/>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { SelectItem } from '@developers/scoro-ui';
import { AppAutoSuggest, AutoSuggestDefaultEmits } from '@/features/App';
import {
	useProjectAutoSuggestLazyQuery,
	type Project,
	ProjectAutoSuggestDocument,
	type User,
	ProjectListOrderByEnum,
} from '@/generated/graphql';
import { DEFAULT_AUTO_SUGGEST_LIMIT } from '@/constants/constants';

export type ProjectAutoSuggestProps = {
	/**
	 * Selected dropdown item to display without fetching all list
	 */
	item?: (Partial<Project> & Pick<Project, 'id' | 'name'>) | undefined;

	autoNavigation?: boolean;

	/**
	 * Exclude projects that given users are already members of
	 */
	excludedUsers?: Array<User['id']>;

	/**
	 * Return only the projects that current user can modify
	 */
	canModify?: boolean;

	/**
	 * Return all projects accessible to the current user
	 */
	ignoreBookmarkUsers?: boolean;
};

const props = withDefaults(defineProps<ProjectAutoSuggestProps>(), {
	item: undefined,
	excludedUsers: () => [],
});

defineEmits(AutoSuggestDefaultEmits);

const {
	loading: isLoading,
	load,
	onResult,
} = useProjectAutoSuggestLazyQuery({
	projectsListInput: {
		filters: {
			searchKeywords: '',
		},
		pager: {
			limit: DEFAULT_AUTO_SUGGEST_LIMIT,
			startFrom: 0,
		},
	},
});

const options = ref<SelectItem[]>([]);

onResult((result) => {
	if (result.data?.ProjectsList) {
		options.value =
			result.data.ProjectsList.map((row) => {
				return {
					id: row.id,
					title: row.name,
				};
			}) || [];
	}
});

const activeItem = computed<SelectItem | undefined>(() => {
	return props.item
		? {
				id: props.item.id,
				title: props.item.name,
			}
		: undefined;
});

const loadOptions = async (searchKeywords: string) => {
	load(
		ProjectAutoSuggestDocument,
		{
			projectsListInput: {
				filters: {
					searchKeywords,
					orderBy: ProjectListOrderByEnum.OrderByProjectName,
					excludedUsers: props.excludedUsers,
					canModify: props.canModify,
					ignoreBookmarkUsers: props.ignoreBookmarkUsers,
				},
				pager: {
					limit: DEFAULT_AUTO_SUGGEST_LIMIT,
					startFrom: 0,
				},
			},
		},
		{
			fetchPolicy:
				props.excludedUsers?.length > 0 ? 'no-cache' : undefined,
		}
	);
};
</script>
